import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Lightbulb, Flag, Clock, X, Sparkles, ChevronRight } from "lucide-react";
import { useEnrichment } from "@/hooks/useEnrichment";
import { useExamTargets } from "@/hooks/useExamTargets";
import { singletonStore } from "@/mocks/localStorageStore";

// Nudge state persists so dismissed/snoozed nudges don't reappear.
interface NudgeState { dismissed: string[]; snoozed: Record<string, number>; }
const store = singletonStore<NudgeState>("nudgeState");
const SNOOZE_DAYS = 7;

interface NudgeDef {
  key: string;
  icon: typeof CalendarClock;
  iconBg: string;
  iconColor: string;
  label: string;
  benefit: string;
  missing: boolean;
}

export function ProfileNudges() {
  const { availability, prefs } = useEnrichment();
  const { targets } = useExamTargets();
  const hasExam = targets.length > 0;
  const [state, setState] = useState<NudgeState>(() => store.get() ?? { dismissed: [], snoozed: {} });

  const defs: NudgeDef[] = [
    {
      key: "availability",
      icon: CalendarClock,
      iconBg: "bg-violet-100",
      iconColor: "text-violet-600",
      label: "Add your availability",
      benefit: "Filter out teachers who can't fit your schedule",
      missing: availability.length === 0,
    },
    {
      key: "learning",
      icon: Lightbulb,
      iconBg: "bg-amber-100",
      iconColor: "text-amber-600",
      label: "Add your learning style",
      benefit: "Get matched with teachers who teach the way you learn",
      missing: !prefs.learningStyle,
    },
    {
      key: "exam",
      icon: Flag,
      iconBg: "bg-rose-100",
      iconColor: "text-rose-600",
      label: "Add an exam goal",
      benefit: "Students who add an exam get more relevant matches",
      missing: !hasExam,
    },
  ];

  const now = Date.now();
  const active = defs
    .filter((d) => d.missing)
    .filter((d) => !state.dismissed.includes(d.key))
    .filter((d) => !(state.snoozed[d.key] && state.snoozed[d.key] > now))
    .slice(0, 3);

  if (active.length === 0) return null;

  const completed = defs.filter((d) => !d.missing).length;
  const pct = Math.round((completed / defs.length) * 100);

  const persist = (next: NudgeState) => { store.set(next); setState(next); };
  const snooze = (key: string) => persist({ ...state, snoozed: { ...state.snoozed, [key]: now + SNOOZE_DAYS * 86400000 } });
  const dismiss = (key: string) => persist({ ...state, dismissed: [...state.dismissed, key] });

  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white/80 backdrop-blur-[2px] shadow-sm overflow-hidden">
      {/* Header with progress */}
      <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/60">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500/15 to-violet-500/15 flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-800">Sharpen your matches</p>
              <span className="text-xs font-semibold text-indigo-600 shrink-0">{completed}/{defs.length} done</span>
            </div>
            {/* Progress bar */}
            <div className="h-1.5 rounded-full bg-slate-200/80 mt-2 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-500"
                style={{ width: `${Math.max(pct, 4)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Nudge rows — whole row clickable, actions revealed on hover */}
      <div className="divide-y divide-slate-100">
        {active.map((n) => {
          const Icon = n.icon;
          return (
            <Link
              key={n.key}
              to="/my-goal"
              className="group flex items-center gap-3.5 px-5 py-3.5 hover:bg-indigo-50/40 transition-colors"
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${n.iconBg}`}>
                <Icon className={`w-4 h-4 ${n.iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">{n.label}</p>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{n.benefit}</p>
              </div>
              {/* Hover actions */}
              <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); snooze(n.key); }}
                  title="Snooze for 7 days"
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                >
                  <Clock className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismiss(n.key); }}
                  title="Dismiss"
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-400 transition-colors shrink-0" />
            </Link>
          );
        })}
      </div>
    </section>
  );
}
